import {
  CloseRunningTaskSUCCESS,
  DeleteTasksSUCCESS,
  GetTasksERROR,
  GetTasksLOADING,
  GetTasksLOADINGMORE,
  GetTasksSUCCESS,
  GetTasksAppendSUCCESS,
  PostTasksSUCCESS,
  SetRunningTask,
  UpdateTasksSUCCESS,
} from "./Task.types";
import axios from "axios";
import { toast } from "react-toastify";
import toastOptions from "../toastOptions";
import { notifyRunningChanged } from "./runningChannel";
import { translate, getStoredLocale, apiErrorMessage } from "../../i18n/translate";

const ENDPOINT = (orgId) => `${process.env.REACT_APP_APIURL}/v1/organizations/${orgId}/time-entries/`;

const RUNNING_ENDPOINT = (orgId) => ENDPOINT(orgId) + "running";

const authConfig = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// The running timer lives on the server (issue #2), so these four actions
// are the only source of truth for it: the tracker input and the running row
// in the list both read state.tasks.running. None of them flips the list's
// loading flag - that would unmount TaskInput mid-timer and throw away the
// description being typed along with the sticky project/billable choice.
export const getRunningApi = (orgId, token) => async (dispatch) => {
  try {
    const { data } = await axios.get(RUNNING_ENDPOINT(orgId), authConfig(token));
    dispatch({ type: SetRunningTask, payload: data });
    return data;
  } catch (e) {
    // 404 simply means no timer is running, which isn't worth a toast.
    dispatch({ type: SetRunningTask, payload: null });
    return null;
  }
};

// syncRunningApi is the resync a left-open tracker runs on focus, on its
// interval and when another tab announces a change. It fetches the same thing
// getRunningApi does, but it also notices when the timer it knew about is
// gone or has been replaced elsewhere: the entry it turned into was closed by
// some other tab or device, so this list doesn't have it and page 1 gets
// pulled again. Quietly - no GetTasksLOADING, for the reason above.
export const syncRunningApi = (orgId, token) => async (dispatch, getState) => {
  const previous = getState().tasks.running;
  let next = null;
  try {
    const { data } = await axios.get(RUNNING_ENDPOINT(orgId), authConfig(token));
    next = data || null;
  } catch (e) {
    // Only a 404 means nothing is running. A network blip or a server error
    // says nothing at all, and acting on one would make a perfectly good
    // timer disappear, so those leave what we have alone.
    if (e.response?.status !== 404) return previous;
  }
  if (previous && previous.id !== next?.id) {
    try {
      const { data } = await axios.get(ENDPOINT(orgId), {
        ...authConfig(token),
        params: { page: 1, pageSize: process.env.REACT_APP_PAGE_SIZE },
      });
      dispatch({ type: GetTasksSUCCESS, payload: data });
    } catch (e) {
      // The list keeps what it has; the running timer still needs updating.
    }
  }
  dispatch({ type: SetRunningTask, payload: next });
  return next;
};

// startRunningApi opens a timer, and closes whatever was already running -
// the server does that in the same call and hands back the finished entries,
// which go straight into the list rather than costing a refetch.
export const startRunningApi = (item, orgId, token) => async (dispatch) => {
  try {
    const { data } = await axios.post(RUNNING_ENDPOINT(orgId), item, authConfig(token));
    dispatch({ type: CloseRunningTaskSUCCESS, payload: data.closed });
    dispatch({ type: SetRunningTask, payload: data.running });
    notifyRunningChanged();
    return data;
  } catch (e) {
    toast.error(apiErrorMessage(e, getStoredLocale()), toastOptions);
  }
};

// Stopping has no success toast, for the same reason creating an entry
// doesn't: it happens every time the timer stops and would be too noisy.
export const stopRunningApi = (overrides, orgId, token) => async (dispatch) => {
  try {
    const { data } = await axios.post(RUNNING_ENDPOINT(orgId) + "/stop", overrides, authConfig(token));
    dispatch({ type: CloseRunningTaskSUCCESS, payload: data.closed });
    dispatch({ type: SetRunningTask, payload: null });
    notifyRunningChanged();
    return data;
  } catch (e) {
    toast.error(apiErrorMessage(e, getStoredLocale()), toastOptions);
  }
};

export const discardRunningApi = (orgId, token) => async (dispatch) => {
  try {
    await axios.delete(RUNNING_ENDPOINT(orgId), authConfig(token));
    dispatch({ type: SetRunningTask, payload: null });
    notifyRunningChanged();
    toast.success(translate(getStoredLocale(), "toasts.timeRecordDeleted"), toastOptions);
  } catch (e) {
    toast.error(apiErrorMessage(e, getStoredLocale()), toastOptions);
  }
};
// page 1 replaces the list (initial load or filter change); page > 1 appends
// (infinite scroll), so each dispatches a different success type to keep
// the reducer's replace/append logic explicit rather than inferred.
export const getTasksApi = (orgId, token, page = 1, pageSize = process.env.REACT_APP_PAGE_SIZE) => async (dispatch) => {
  dispatch({ type: page === 1 ? GetTasksLOADING : GetTasksLOADINGMORE });
  try {
    const { data } = await axios.get(ENDPOINT(orgId), { ...authConfig(token), params: { page, pageSize } });
    dispatch({
      type: page === 1 ? GetTasksSUCCESS : GetTasksAppendSUCCESS,
      payload: data,
    });
    return data;
  } catch (e) {
    dispatch({
      type: GetTasksERROR,
    });
  }
};
// fetchLatestEntryDayApi resolves the caller's most recently logged time
// entry's day ("YYYY-MM-DD"), or null if they have none - doesn't touch the
// shared tasks list state (unlike getTasksApi), since it's just a lookup
// used by Reports.jsx to default its date range to the user's last logged
// activity instead of today's date (ai-instruct-63).
export const fetchLatestEntryDayApi = (orgId, token) => async () => {
  try {
    const { data } = await axios.get(ENDPOINT(orgId), { ...authConfig(token), params: { page: 1, pageSize: 1 } });
    return data.items?.[0]?.day ?? null;
  } catch (e) {
    return null;
  }
};

// Time record creation intentionally has no success toast: it fires every
// time the timer is stopped, which would be too noisy.
// Quietly, like the running-timer actions above: the list's loading flag
// would swap the page for a spinner, unmounting TaskInput and throwing away
// the sticky project, day and billable choice (and the typed values when the
// save fails).
export const postTasksApi = (item, orgId, token) => async (dispatch) => {
  try {
    const { data } = await axios.post(ENDPOINT(orgId), item, authConfig(token));
    dispatch({ type: PostTasksSUCCESS, payload: data });
    return data;
  } catch (e) {
    toast.error(apiErrorMessage(e, getStoredLocale()), toastOptions);
  }
};
export const deleteTasksApi = (id, orgId, token) => async (dispatch) => {
  dispatch({
    type: GetTasksLOADING,
  });
  try {
    await axios.delete(ENDPOINT(orgId) + id, authConfig(token));
    dispatch({ type: DeleteTasksSUCCESS, payload: id });
    toast.success(translate(getStoredLocale(), "toasts.timeRecordDeleted"), toastOptions);
  } catch (e) {
    dispatch({
      type: GetTasksERROR,
    });
    toast.error(apiErrorMessage(e, getStoredLocale()), toastOptions);
  }
};
export const updateTasksApi = (task, orgId, token) => async (dispatch) => {
  dispatch({
    type: GetTasksLOADING,
  });
  try {
    const { id } = task;
    const { data } = await axios.put(ENDPOINT(orgId) + id, task, authConfig(token));
    dispatch({ type: UpdateTasksSUCCESS, payload: data });
    toast.success(translate(getStoredLocale(), "toasts.timeRecordUpdated"), toastOptions);
  } catch (e) {
    dispatch({
      type: GetTasksERROR,
    });
    toast.error(apiErrorMessage(e, getStoredLocale()), toastOptions);
  }
};
